import { useCallback, useEffect, useRef, useState } from 'react'
import {
    Box,
    CircularProgress,
    IconButton,
    LinearProgress,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import VideocamIcon from '@mui/icons-material/Videocam'
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined'
import BugReportIcon from '@mui/icons-material/BugReportOutlined'
import StopIcon from '@mui/icons-material/Stop'
import SendIcon from '@mui/icons-material/Send'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useDispatch, useSelector } from 'react-redux'
import { useStyles } from './styles'
import { NonInteractiveTooltip } from '../custom'
import { FeedbackCategory } from '../../proto/generated/user-feedback'
import { ScreenshotEditorOverlay } from './ScreenshotEditorOverlay'
import {
    closeFeedbackPanel,
    setCategory,
    setDescription,
    addAttachment,
    removeAttachment,
    setCapturing,
    setCaptureError,
    collapsePanelForCapture,
    expandPanelAfterCapture,
    resetFeedbackPanel,
    setPanelEntered,
    MAX_ATTACHMENTS,
    FeedbackMediaAttachment
} from '../../reducer/feedback.reducer'
import { captureScreenshotFrame, captureVideo, MAX_VIDEO_SECONDS } from '../../util/media'
import { uploadAttachments, submitFeedback } from '../../util/uploadMedia'
import { selectFeedbackStore } from '../../selectors/feedback'

const PANEL_RIGHT = 64
const PANEL_BOTTOM = 12

function MediaThumbnail({
    attachment,
    index,
    progress,
    onRemove
}: {
    attachment: FeedbackMediaAttachment
    index: number
    progress: number | null
    onRemove: (index: number) => void
}) {
    const { classes } = useStyles()
    const isUploading = progress !== null && progress < 100

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                component={attachment.type === 'screenshot' ? 'img' : 'video'}
                src={attachment.url}
                className={classes.mediaThumbnail}
                {...(attachment.type === 'video' && { controls: false, muted: true, playsInline: true })}
                sx={{ opacity: isUploading ? 0.5 : 1, transition: 'opacity 0.2s' }}
            />
            {progress !== null && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderBottomLeftRadius: 4,
                        borderBottomRightRadius: 4,
                        overflow: 'hidden'
                    }}
                >
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 3 }}
                    />
                </Box>
            )}
            {!isUploading && (
                <IconButton
                    size="small"
                    onClick={() => onRemove(index)}
                    aria-label={`Remove ${attachment.type}`}
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        padding: '2px',
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                >
                    <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
            )}
        </Box>
    )
}

export function FeedbackPanel() {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const { isExpanded, isClosing, panelEntered, category, description, attachments, isCapturing, captureError } =
        useSelector(selectFeedbackStore)
    const [screenshotCanvas, setScreenshotCanvas] = useState<HTMLCanvasElement | null>(null)
    const [isRecording, setIsRecording] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<number[] | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [uploadError, setUploadError] = useState<string | null>(null)
    const [submitted, setSubmitted] = useState(false)
    const stopRecordingRef = useRef<(() => void) | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const isUploading = uploadProgress !== null
    const atLimit = attachments.length >= MAX_ATTACHMENTS
    const isBusy = isCapturing || isUploading || isSubmitting

    useEffect(() => {
        if (isExpanded && !isClosing) {
            dispatch(setPanelEntered(false))
            const raf = requestAnimationFrame(() => {
                requestAnimationFrame(() => dispatch(setPanelEntered(true)))
            })
            return () => cancelAnimationFrame(raf)
        }
    }, [isExpanded, isClosing, dispatch])

    useEffect(() => {
        if (!isClosing) return
        const timer = setTimeout(() => {
            dispatch(resetFeedbackPanel())
            setSubmitted(false)
            setUploadError(null)
            setUploadProgress(null)
            setIsSubmitting(false)
        }, 220)
        return () => clearTimeout(timer)
    }, [isClosing, dispatch])

    const handleClose = useCallback(() => {
        dispatch(closeFeedbackPanel())
    }, [dispatch])

    const handleCategoryChange = useCallback(
        (_e: React.MouseEvent<HTMLElement>, value: FeedbackCategory | null) => {
            if (value !== null) dispatch(setCategory(value))
        },
        [dispatch]
    )

    const handleScreenshot = useCallback(async () => {
        dispatch(setCaptureError(null))
        dispatch(setCapturing(true))
        dispatch(collapsePanelForCapture())
        try {
            const canvas = await captureScreenshotFrame()
            setScreenshotCanvas(canvas)
        } catch (err) {
            dispatch(setCaptureError(err instanceof Error ? err.message : 'Screenshot failed'))
            dispatch(expandPanelAfterCapture())
        } finally {
            dispatch(setCapturing(false))
        }
    }, [dispatch])

    const handleScreenshotDone = useCallback(
        (blob: Blob) => {
            const url = URL.createObjectURL(blob)
            dispatch(addAttachment({ type: 'screenshot', url }))
            setScreenshotCanvas(null)
            dispatch(expandPanelAfterCapture())
        },
        [dispatch]
    )

    const handleScreenshotCancel = useCallback(() => {
        setScreenshotCanvas(null)
        dispatch(expandPanelAfterCapture())
    }, [dispatch])

    const handleVideo = useCallback(async () => {
        dispatch(setCaptureError(null))
        dispatch(setCapturing(true))
        dispatch(collapsePanelForCapture())
        const stopPromise = new Promise<void>((resolve) => {
            stopRecordingRef.current = resolve
        })
        try {
            setIsRecording(true)
            const blob = await captureVideo(stopPromise)
            const url = URL.createObjectURL(blob)
            dispatch(addAttachment({ type: 'video', url }))
            dispatch(expandPanelAfterCapture())
        } catch (err) {
            dispatch(setCaptureError(err instanceof Error ? err.message : 'Video capture failed'))
            dispatch(expandPanelAfterCapture())
        } finally {
            setIsRecording(false)
            stopRecordingRef.current = null
            dispatch(setCapturing(false))
        }
    }, [dispatch])

    const handleRemoveAttachment = useCallback(
        (index: number) => {
            dispatch(removeAttachment(index))
        },
        [dispatch]
    )

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? [])
            const remaining = MAX_ATTACHMENTS - attachments.length
            const allowed = ['image/png', 'image/jpeg', 'image/webp', 'video/webm', 'video/mp4', 'video/quicktime']
            const valid = files.filter((f) => allowed.includes(f.type))
            valid.slice(0, remaining).forEach((file) => {
                const type = file.type.startsWith('video/') ? 'video' : 'screenshot'
                const url = URL.createObjectURL(file)
                dispatch(addAttachment({ type, url }))
            })
            e.target.value = ''
        },
        [attachments.length, dispatch]
    )

    const handleSend = useCallback(async () => {
        setUploadError(null)
        let uploadResults: Awaited<ReturnType<typeof uploadAttachments>> = []
        if (attachments.length > 0) {
            setUploadProgress(attachments.map(() => 0))
            uploadResults = await uploadAttachments(attachments, (index, pct) => {
                setUploadProgress((prev) => {
                    if (!prev) return prev
                    const next = [...prev]
                    next[index] = pct
                    return next
                })
            })
            setUploadProgress(null)
        }
        try {
            setIsSubmitting(true)
            await submitFeedback(category, description, uploadResults)
            setSubmitted(true)
        } catch (err) {
            setUploadError('Error submitting your feedback. Please try again.')
            setIsSubmitting(false)
        }
    }, [attachments, category, description, dispatch])

    if (!isExpanded && !isClosing && !screenshotCanvas && !isRecording) {
        return null
    }

    return (
        <>
            {screenshotCanvas && (
                <ScreenshotEditorOverlay
                    canvas={screenshotCanvas}
                    onDone={handleScreenshotDone}
                    onCancel={handleScreenshotCancel}
                />
            )}
            {isRecording && (
                <Box
                    data-html2canvas-ignore
                    sx={{
                        position: 'fixed',
                        bottom: 32,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10001,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                        px: 2.5,
                        py: 1,
                        boxShadow: 6
                    }}
                >
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            animation: 'feedbackRecordPulse 1s ease-in-out infinite'
                        }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                        Recording…
                    </Typography>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => stopRecordingRef.current?.()}
                        aria-label="Stop recording"
                        sx={{ ml: 0.5 }}
                    >
                        <StopIcon fontSize="small" />
                    </IconButton>
                    <style>{`
                        @keyframes feedbackRecordPulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.3; }
                        }
                    `}</style>
                </Box>
            )}
            <Box
                className={classes.chatPanel}
                data-html2canvas-ignore
                sx={{
                    right: `${PANEL_RIGHT}px`,
                    bottom: `${PANEL_BOTTOM}px`,
                    left: 'auto',
                    top: 'auto',
                    transformOrigin: 'bottom right',
                    opacity: panelEntered && !isClosing ? 1 : 0,
                    transform: panelEntered && !isClosing ? 'scale(1)' : 'scale(0.9)'
                }}
            >
                <Box className={classes.chatHeader}>
                    <Typography className={classes.chatTitle} variant="body1">
                        Feedback
                    </Typography>
                    <IconButton size="small" onClick={handleClose} aria-label="Close feedback">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box className={classes.chatContent}>
                    {submitted ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                py: 4
                            }}
                        >
                            <CheckCircleOutlineIcon sx={{ fontSize: 52, color: 'success.main' }} />
                            <Typography variant="body2" align="center" color="text.secondary">
                                {category === FeedbackCategory.BUG
                                    ? 'Thank you for reporting the error'
                                    : 'Thank you for your feedback'}
                            </Typography>
                        </Box>
                    ) : (
                    <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Type
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            value={category}
                            exclusive
                            onChange={handleCategoryChange}
                            fullWidth
                            sx={{ height: 36 }}
                        >
                            <ToggleButton value={FeedbackCategory.FEEDBACK} aria-label="Feedback">
                                <FeedbackIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                Feedback
                            </ToggleButton>
                            <ToggleButton value={FeedbackCategory.BUG} aria-label="Bug">
                                <BugReportIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                Bug
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Description
                        </Typography>
                        <TextField
                            multiline
                            minRows={4}
                            maxRows={8}
                            placeholder={
                                category === FeedbackCategory.BUG
                                    ? 'Describe the bug and steps to reproduce...'
                                    : 'Share your feedback or suggestions...'
                            }
                            value={description}
                            onChange={(e) => dispatch(setDescription(e.target.value))}
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                    </Box>
                    {attachments.length > 0 && (
                        <Box
                            sx={{
                                mb: 2,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 2.5
                            }}
                        >
                            {attachments.map((attachment, index) => (
                                <MediaThumbnail
                                    key={attachment.url}
                                    attachment={attachment}
                                    index={index}
                                    progress={uploadProgress ? uploadProgress[index] ?? 0 : null}
                                    onRemove={handleRemoveAttachment}
                                />
                            ))}
                        </Box>
                    )}
                    {(captureError || uploadError) && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                            {captureError ?? uploadError}
                        </Typography>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,video/webm,video/mp4,video/quicktime"
                        multiple
                        hidden
                        onChange={handleFileChange}
                    />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto', pt: 1 }}>
                        <NonInteractiveTooltip
                            arrow
                            placement="top"
                            title={atLimit ? `Max ${MAX_ATTACHMENTS} attachments reached` : 'Attach file'}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isBusy || atLimit}
                                    aria-label="Attach file"
                                >
                                    <AttachFileIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </NonInteractiveTooltip>
                        <NonInteractiveTooltip
                            arrow
                            placement="top"
                            title={atLimit ? `Max ${MAX_ATTACHMENTS} attachments reached` : 'Take screenshot'}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleScreenshot}
                                    disabled={isBusy || atLimit}
                                    aria-label="Take screenshot"
                                >
                                    <PhotoCameraIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </NonInteractiveTooltip>
                        <NonInteractiveTooltip
                            arrow
                            placement="top"
                            title={atLimit ? `Max ${MAX_ATTACHMENTS} attachments reached` : `Record video (max ${MAX_VIDEO_SECONDS}s)`}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleVideo}
                                    disabled={isBusy || atLimit}
                                    aria-label="Record video"
                                >
                                    <VideocamIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </NonInteractiveTooltip>
                        {atLimit && (
                            <Typography variant="caption" color="text.disabled">
                                {MAX_ATTACHMENTS}/{MAX_ATTACHMENTS}
                            </Typography>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <NonInteractiveTooltip
                            arrow
                            placement="top"
                            title={isUploading ? 'Uploading…' : isSubmitting ? 'Submitting…' : 'Send'}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={handleSend}
                                    disabled={isBusy}
                                    aria-label="Send feedback"
                                >
                                    {(isUploading || isSubmitting)
                                        ? <CircularProgress size={18} color="primary" />
                                        : <SendIcon fontSize="small" />
                                    }
                                </IconButton>
                            </span>
                        </NonInteractiveTooltip>
                    </Stack>
                    </>
                    )}
                </Box>
            </Box>
        </>
    )
}
